import PaywallPlanCard from '@/components/v1/PaywallPlanCard'
import { type PaywallPlan } from '@/components/v1/paywallPricing'
import SubscriptionForm from '@app-studio-web/ui/components/SubscriptionForm'
import SubscriptionTerms from '@app-studio-web/ui/components/SubscriptionTerms'
import { type Plan } from '@app-studio-web/ui/types'
import { ShieldCheckIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'

const PAYMENT_METHODS = ['VISA', 'Mastercard', 'AMEX', 'Apple Pay', 'G Pay']

interface Props {
  currency: string
  isSubmitting: boolean
  onPlanChange: (plan: Plan) => void
  paywallPlans: PaywallPlan[]
  popularPlan: Plan
  selectedPaywallPlan: PaywallPlan
  setIsSubmitting: (isSubmitting: boolean) => void
}

export default function PaywallPlans({
  currency,
  isSubmitting,
  onPlanChange,
  paywallPlans,
  popularPlan,
  selectedPaywallPlan,
  setIsSubmitting,
}: Props) {
  const { t } = useTranslation()

  return (
    <div className="hw-card space-y-4 p-3">
      <h2 className="hw-heading pt-2">{t('pages.checkout.v1.choosePlan')}</h2>

      <div className="space-y-3">
        {paywallPlans.map((paywallPlan, index) => (
          <PaywallPlanCard
            animationDelayMs={index * 80}
            currency={currency}
            isPopular={paywallPlan.plan === popularPlan}
            isSelected={paywallPlan.plan === selectedPaywallPlan.plan}
            key={paywallPlan.plan}
            onSelect={() => onPlanChange(paywallPlan.plan)}
            paywallPlan={paywallPlan}
          />
        ))}
      </div>

      <SubscriptionForm
        buttonClassName="hw-button-primary animate-pulse-cta"
        buttonTitle={t('pages.checkout.v1.getMyPlan')}
        currency={currency}
        isSubmitting={isSubmitting}
        plan={selectedPaywallPlan.plan}
        priceId={selectedPaywallPlan.intro?.id ?? selectedPaywallPlan.full.id}
        setIsSubmitting={setIsSubmitting}
        stripePriceFull={selectedPaywallPlan.full}
      />

      <div className="flex flex-col items-center gap-3">
        <div className="flex flex-wrap justify-center gap-1">
          {PAYMENT_METHODS.map((method) => (
            <span
              className="border-hw-line text-hw-muted rounded border bg-white px-1.5 py-0.5 text-[9px] font-bold whitespace-nowrap"
              key={method}
            >
              {method}
            </span>
          ))}
        </div>

        <p className="bg-hw-green-soft text-hw-green flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium">
          <ShieldCheckIcon className="h-4 w-4" />
          {t('pages.checkout.payment.securePaymentTitle')}
        </p>

        {selectedPaywallPlan.plan !== 'one-time' && (
          <p className="text-hw-muted text-center text-xs">
            {t('pages.checkout.plans.cancelAnytime')}
          </p>
        )}

        <p className="text-hw-ink text-center text-xs font-semibold">
          {t('pages.checkout.plans.moneyBackGuarantee')}
        </p>
      </div>

      <SubscriptionTerms
        currency={currency}
        plan={selectedPaywallPlan.plan}
        stripePriceFull={selectedPaywallPlan.full}
        stripePriceIntro={selectedPaywallPlan.intro}
      />
    </div>
  )
}
